import { Auth, connectAuthEmulator } from 'firebase/auth';
import { connectFirestoreEmulator, Firestore } from 'firebase/firestore';
import { getLogger } from '../logger';

/**
 * Test-only wiring that points the Firebase SDK at the local emulator suite.
 *
 * This is a purpose-built mode for verifying the passkey flow (`npm run dev:passkeys`), NOT the
 * pre-existing `start-emulator` script -- that one deliberately leaves the app pointed at production
 * Firestore, and this doesn't change it.
 *
 * Auth and Firestore have to be emulated together: production Firestore rejects tokens minted by
 * the Auth emulator, so emulating only Auth would leave the app unable to read anything. The
 * consequence is that this mode has no real trip data, which is why it exists only to exercise the
 * auth ceremony rather than to run the app.
 *
 * Everything the emulators need to be known -- the env vars, the host, the ports, the failure
 * handling -- lives here rather than in lib/firebase.ts, which only calls the two hooks below at
 * the points where it constructs the SDK objects. Connecting an emulator is only legal at
 * construction time, so some seam in that file is unavoidable; the seam carries no emulator
 * knowledge of its own. Under a production build NEXT_PUBLIC_PASSKEY_EMULATOR is unset, so both
 * hooks compile down to a constant-false branch.
 */
const logger = getLogger('firebase');

const useEmulators = process.env.NEXT_PUBLIC_PASSKEY_EMULATOR === 'true';
const emulatorHost = process.env.NEXT_PUBLIC_FIREBASE_EMULATOR_HOST ?? 'localhost';

// Deliberately offset from the ports `start-emulator` uses (auth 9099, functions 5001,
// firestore 8080, storage 9199) so both suites can run at once. Must match server/firebase.json.
const AUTH_EMULATOR_PORT = 9399;
const FIRESTORE_EMULATOR_PORT = 8380;

/**
 * Points Firestore at the emulator. Must run before anything reads from it -- connecting an
 * emulator after first use throws.
 */
export function connectFirestoreEmulatorIfConfigured(firestore: Firestore): void {
    if (!useEmulators) {
        return;
    }
    try {
        connectFirestoreEmulator(firestore, emulatorHost, FIRESTORE_EMULATOR_PORT);
        logger.log(`Connected Firestore to the emulator at ${emulatorHost}:${FIRESTORE_EMULATOR_PORT}`);
    } catch (err) {
        // Hot reload can re-run initialization against an already-connected instance. Not fatal.
        logger.warn(`Could not connect the Firestore emulator: ${err}`);
    }
}

/**
 * Points Auth at the emulator, immediately after initializeAuth().
 *
 * Separate from Firestore because this app calls initializeAuth() lazily, in useFirebaseAuth, to
 * force indexedDB persistence -- touching auth alongside Firestore would initialize it first with
 * the default persistence and make that call fail.
 */
export function connectAuthEmulatorIfConfigured(auth: Auth): void {
    if (!useEmulators) {
        return;
    }
    try {
        // disableWarnings only silences the SDK's unsecured-traffic banner; the connection itself
        // is unaffected. Against the emulator, custom tokens are unsigned -- which is what lets the
        // passkey flow (which ends in createCustomToken) run locally without production credentials
        // or the iam.serviceAccounts.signBlob permission.
        connectAuthEmulator(auth, `http://${emulatorHost}:${AUTH_EMULATOR_PORT}`, { disableWarnings: true });
        logger.log(`Connected Auth to the emulator at ${emulatorHost}:${AUTH_EMULATOR_PORT}`);
    } catch (err) {
        logger.warn(`Could not connect the Auth emulator: ${err}`);
    }
}
